import { createTheme } from '@mui/material/styles';

declare module '@mui/material/styles' {
  interface Theme {
    custom: {
      sectionBackgrounds: {
        section1: string;
        section2: string;
        section3: string;
        section4: string;
      };
    };
  }
  interface ThemeOptions {
    custom?: {
      sectionBackgrounds?: {
        section1?: string;
        section2?: string;
        section3?: string;
        section4?: string;
      };
    };
  }
}

// Custom fonts configuration
const fontFamily = {
  primary: '"Poppins", Helvetica, Arial, sans-serif',
  secondary: '"Montserrat", Helvetica, Arial, sans-serif',
};

// Dark color palette with black background
const colors = {
  background: '#000000',
  text: '#ffffff',
  primary: '#ffffff',
  secondary: '#cccccc',
  accent: '#333333',
  cardBg: '#1a1a1a',
  inputBg: '#1a1a1a',
  border: '#333333',
  section1: '#000000',
  section2: '#0a0a0a',
  section3: '#141414',
  section4: '#1a1a1a',
};

// Create dark theme
export const darkTheme = createTheme({
  palette: {
    mode: 'dark',
    primary: {
      main: colors.primary,
      contrastText: colors.background,
    },
    secondary: {
      main: colors.secondary,
      contrastText: colors.background,
    },
    background: {
      default: colors.background,
      paper: colors.cardBg,
    },
    text: {
      primary: colors.text,
      secondary: '#CCCCCC',
    },
    divider: colors.border,
  },
  typography: {
    fontFamily: fontFamily.primary,
    h1: {
      fontFamily: fontFamily.secondary,
      fontWeight: 600,
      color: colors.primary,
    },
    h2: {
      fontFamily: fontFamily.secondary,
      fontWeight: 600,
      color: colors.primary,
    },
    h3: {
      fontFamily: fontFamily.secondary,
      fontWeight: 600,
      color: colors.primary,
    },
    h4: {
      fontFamily: fontFamily.secondary,
      fontWeight: 600,
      color: colors.primary,
    },
    h5: {
      fontFamily: fontFamily.secondary,
      fontWeight: 600,
      color: colors.primary,
    },
    h6: {
      fontFamily: fontFamily.secondary,
      fontWeight: 600,
      color: colors.primary,
    },
    button: {
      textTransform: 'none',
      fontWeight: 500,
    },
  },
  shape: {
    borderRadius: 8,
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          padding: '10px 20px',
          fontSize: '16px',
          fontWeight: 500,
          '&:hover': {
            transform: 'translateY(-1px)',
            boxShadow: '0 4px 8px rgba(0, 0, 0, 0.3)',
          },
        },
        contained: {
          backgroundColor: colors.secondary,
          color: colors.background,
          '&:hover': {
            backgroundColor: colors.primary,
          },
        },
        outlined: {
          borderColor: colors.primary,
          color: colors.primary,
          '&:hover': {
            backgroundColor: 'rgba(255, 69, 0, 0.1)',
            borderColor: colors.secondary,
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          backgroundColor: colors.cardBg,
          borderRadius: 12,
          boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
          transition: 'all 0.3s ease',
          '&:hover': {
            transform: 'translateY(-4px)',
            boxShadow: '0 8px 16px rgba(0, 0, 0, 0.2)',
          },
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            backgroundColor: colors.inputBg,
            borderRadius: 8,
            '& fieldset': {
              borderColor: colors.border,
            },
            '&:hover fieldset': {
              borderColor: colors.primary,
            },
            '&.Mui-focused fieldset': {
              borderColor: colors.primary,
            },
          },
          '& .MuiInputLabel-root': {
            color: colors.text,
          },
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: 'rgba(42, 42, 42, 0.8)',
          backdropFilter: 'blur(10px)',
        },
      },
    },
  },
  custom: {
    sectionBackgrounds: {
      section1: colors.section1,
      section2: colors.section2,
      section3: colors.section3,
      section4: colors.section4,
    },
  },
});

// Light color palette with light background
const lightColors = {
  background: '#ffffff',
  text: '#000000',
  primary: '#000000',
  secondary: '#333333',
  accent: '#cccccc',
  cardBg: '#f5f5f5',
  inputBg: '#f5f5f5',
  border: '#cccccc',
  section1: '#ffffff',
  section2: '#fafafa',
  section3: '#f5f5f5',
  section4: '#f0f0f0',
};

// Complete light theme
export const lightTheme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: lightColors.primary,
      contrastText: lightColors.background,
    },
    secondary: {
      main: lightColors.secondary,
      contrastText: lightColors.background,
    },
    background: {
      default: lightColors.background,
      paper: lightColors.cardBg,
    },
    text: {
      primary: lightColors.text,
      secondary: '#333333',
    },
    divider: lightColors.border,
  },
  typography: {
    fontFamily: fontFamily.primary,
    h1: {
      fontFamily: fontFamily.secondary,
      fontWeight: 600,
      color: lightColors.primary,
    },
    h2: {
      fontFamily: fontFamily.secondary,
      fontWeight: 600,
      color: lightColors.primary,
    },
    h3: {
      fontFamily: fontFamily.secondary,
      fontWeight: 600,
      color: lightColors.primary,
    },
    h4: {
      fontFamily: fontFamily.secondary,
      fontWeight: 600,
      color: lightColors.primary,
    },
    h5: {
      fontFamily: fontFamily.secondary,
      fontWeight: 600,
      color: lightColors.primary,
    },
    h6: {
      fontFamily: fontFamily.secondary,
      fontWeight: 600,
      color: lightColors.primary,
    },
    button: {
      textTransform: 'none',
      fontWeight: 500,
    },
  },
  shape: {
    borderRadius: 8,
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          padding: '10px 20px',
          fontSize: '16px',
          fontWeight: 500,
          '&:hover': {
            transform: 'translateY(-1px)',
            boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
          },
        },
        contained: {
          backgroundColor: lightColors.secondary,
          color: lightColors.background,
          '&:hover': {
            backgroundColor: lightColors.primary,
          },
        },
        outlined: {
          borderColor: lightColors.primary,
          color: lightColors.primary,
          '&:hover': {
            backgroundColor: 'rgba(0, 0, 0, 0.1)',
            borderColor: lightColors.secondary,
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          backgroundColor: lightColors.cardBg,
          borderRadius: 12,
          boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
          transition: 'all 0.3s ease',
          '&:hover': {
            transform: 'translateY(-4px)',
            boxShadow: '0 8px 16px rgba(0, 0, 0, 0.1)',
          },
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            backgroundColor: lightColors.inputBg,
            borderRadius: 8,
            '& fieldset': {
              borderColor: lightColors.border,
            },
            '&:hover fieldset': {
              borderColor: lightColors.primary,
            },
            '&.Mui-focused fieldset': {
              borderColor: lightColors.primary,
            },
          },
          '& .MuiInputLabel-root': {
            color: lightColors.text,
          },
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: 'rgba(245, 245, 245, 0.8)',
          backdropFilter: 'blur(10px)',
        },
      },
    },
  },
  custom: {
    sectionBackgrounds: {
      section1: lightColors.section1,
      section2: lightColors.section2,
      section3: lightColors.section3,
      section4: lightColors.section4,
    },
  },
});

export default darkTheme;
